import { useState, type ReactNode } from "react"
import { createPortal } from "react-dom"
import * as Icons from "@nova/component-icons"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { ButtonPrimary } from "@nova/component-btn"
import { TriCheckbox } from "@nova/component-checkbox"
import {
    GroundStrikeGlyph,
    PotentialStrikeGlyph,
    RISK_SWATCH_GRADIENT_CSS,
} from "./LightningRiskLegend"

export const LIGHTNING_STRIKE_DATA_DONT_SHOW_KEY = "lightning-strike-data-modal-dont-show-again"

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
    <div className="flex flex-col gap-3">
        <span className="text-xs uppercase tracking-wide text-gray-300">{title}</span>
        {children}
    </div>
)

const SymbologyRow = ({
    glyph,
    label,
    description,
}: {
    glyph: ReactNode
    label: string
    description: string
}) => (
    <div className="flex items-start gap-3">
        <span className="flex h-5 w-6 shrink-0 items-center justify-center">{glyph}</span>
        <div className="flex flex-col gap-0.5">
            <span className="text-sm text-gray-0">{label}</span>
            <span className="text-sm text-gray-300">{description}</span>
        </div>
    </div>
)

export const LightningStrikeDataModal = ({ onClose }: { onClose: () => void }) => {
    const [dontShowAgain, setDontShowAgain] = useState(false)

    const close = () => {
        if (dontShowAgain) localStorage.setItem(LIGHTNING_STRIKE_DATA_DONT_SHOW_KEY, "true")
        onClose()
    }

    return createPortal(
        <HighPriorityNovaModalContainer
            title="Understanding Lightning Strike Data"
            closeModal={close}
            className="w-[480px]"
            bottomBar={
                <div className="flex w-full items-center justify-between">
                    <div
                        className="group flex cursor-pointer items-center gap-2"
                        onClick={() => setDontShowAgain(!dontShowAgain)}
                    >
                        <TriCheckbox state={dontShowAgain ? "checked" : "unchecked"} />
                        <h5 className="text-gray-100 group-hover:text-gray-0">Do not show again</h5>
                    </div>
                    <ButtonPrimary onClick={close} title="Continue" />
                </div>
            }
        >
            <div className="flex flex-col gap-5 pt-2">
                <span className="text-sm text-gray-0">
                    Track lightning risk before it happens, or detected strikes as they occur.
                    Confirmed strikes only appear when zoomed in on the map in the past or current
                    time.
                </span>

                <Section title="Lightning risk">
                    <div className="flex items-start gap-3">
                        <span
                            className="mt-0.5 h-7 w-7 shrink-0 rounded"
                            style={{
                                background: `linear-gradient(135deg, ${RISK_SWATCH_GRADIENT_CSS})`,
                            }}
                        />
                        <div className="flex flex-col gap-1">
                            <span className="text-sm text-gray-0">
                                Colors represent lightning risk based on atmospheric instability and
                                precipitation.
                            </span>
                            <span className="text-sm text-gray-300">
                                View observed or forecasted risk by moving the timeline.
                            </span>
                        </div>
                    </div>
                </Section>

                <Section title="Strike symbology">
                    <SymbologyRow
                        glyph={<PotentialStrikeGlyph isLight={false} className="h-4 w-4" />}
                        label="Potential strikes"
                        description="Lightning seen by satellite, not confirmed as a ground strike."
                    />
                    <SymbologyRow
                        glyph={<GroundStrikeGlyph className="h-4 w-4" />}
                        label="Confirmed ground strike"
                        description="Detected by a ground sensor network, carrying peak current in kA."
                    />
                </Section>

                <span className="text-sm text-gray-0">
                    Inside covered areas, every strike shown is confirmed by the ground sensor
                    network, updated every few minutes around the most active storms. Outside them,
                    only potential strikes appear, and some ground strikes may go undetected.
                </span>

                <span className="text-sm text-gray-300">
                    Turn on Strike area coverage in the settings
                    <Icons.SmallIcon
                        Icon={Icons.Gear}
                        className="!inline-block mx-1 -mb-0.5 text-gray-300"
                    />
                    to see which areas have ground sensor detection.
                </span>
            </div>
        </HighPriorityNovaModalContainer>,
        document.getElementById("portal") ?? document.body
    )
}
